import api from './api';
import {
  BusinessCardNoteRequest,
  BusinessCardNoteResponse,
  HandwrittenNoteRequest,
  HandwrittenNoteResponse,
  TextNoteRequest,
  TextNoteResponse,
  ProcessTextRequest,
  ProcessTextResponse,
  VoiceNoteRequest,
  VoiceNoteResponse,
  Note,
} from '@/types';

export const notesApi = {
  /**
   * Upload business card for OCR processing via Express API
   */
  uploadBusinessCard: async (
    noteData: BusinessCardNoteRequest
  ): Promise<BusinessCardNoteResponse> => {
    const { data } = await api.post<BusinessCardNoteResponse>(
      '/notes/business-card',
      noteData
    );
    return data;
  },

  /**
   * Upload handwritten note via Express API
   */
  uploadHandwrittenNote: async (
    noteData: HandwrittenNoteRequest
  ): Promise<HandwrittenNoteResponse> => {
    const { data } = await api.post<HandwrittenNoteResponse>(
      '/notes/handwritten',
      noteData
    );
    return data;
  },

  /**
   * Process text with AI (no save to DB)
   */
  processTextWithAI: async (
    request: ProcessTextRequest
  ): Promise<ProcessTextResponse> => {
    const { data } = await api.post<ProcessTextResponse>(
      '/notes/text/process-ai',
      request
    );
    return data;
  },

  /**
   * Upload text note (save to DB, no AI processing)
   */
  uploadTextNote: async (
    noteData: TextNoteRequest
  ): Promise<TextNoteResponse> => {
    const { data } = await api.post<TextNoteResponse>(
      '/notes/text',
      noteData
    );
    return data;
  },

  /**
   * Upload voice note for transcription via Express API
   */
  uploadVoiceNote: async (
    noteData: VoiceNoteRequest
  ): Promise<VoiceNoteResponse> => {
    const { data } = await api.post<VoiceNoteResponse>(
      '/notes/voice',
      noteData
    );
    return data;
  },

  /**
   * Get all notes for a meeting
   */
  getNotes: async (meetingId: number): Promise<{ success: boolean; notes: Note[] }> => {
    const { data } = await api.get<{ success: boolean; notes: Note[] }>(
      '/notes',
      {
        params: { meetingId }
      }
    );
    return data;
  },

  /**
   * Update a note
   */
  updateNote: async (
    noteId: number,
    noteData: { content?: string; transcription?: string; businessCardData?: any }
  ): Promise<{ success: boolean; message: string; note: Note }> => {
    const { data } = await api.put<{ success: boolean; message: string; note: Note }>(
      `/notes/${noteId}`,
      noteData
    );
    return data;
  },

  /**
   * Delete a note
   */
  deleteNote: async (noteId: number): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.delete<{ success: boolean; message: string }>(
      `/notes/${noteId}`
    );
    return data;
  },

  /**
   * Retry transcription for failed voice note
   */
  retryTranscription: async (noteId: number): Promise<{ success: boolean; message: string; note: Note }> => {
    const { data } = await api.post<{ success: boolean; message: string; note: Note }>(
      `/notes/${noteId}/retry-transcription`
    );
    return data;
  },
};
